import { motion, useReducedMotion } from "motion/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  usePlotArea,
  useYAxisScale,
} from "recharts";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  BookOpen,
  ChartNoAxesCombined,
  CirclePlay,
  Copy,
  CopyCheck,
  Inbox,
  Search,
  SearchX,
  UsersRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "components/ui/button";
import { EmptyState } from "components/ui/empty-state";
import { HelpVideoDialog } from "components/ui/help-video-dialog";
import { Badge } from "components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Skeleton } from "components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { Tooltip as DashboardTooltip } from "components/ui/tooltip";
import integrationCatalog, { getCanonicalProviderKey } from "components/Integrations/integrationCatalog";
import { EASE_OUT } from "lib/ease";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import "./dashboard.css";

const HELP_ARTICLE_URL = "https://groupboss.io/help/install-groupboss-chrome-extension/";
const SETUP_VIDEO_URL = "https://vimeo.com/1147895186?fl=ip&fe=ec";

const formatNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "0";
};

const compactNumberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
  notation: "compact",
});

const formatCompactNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? compactNumberFormatter.format(number) : "0";
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(date);
};

const getProviderLabel = (provider) =>
  String(provider)
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const integrationCatalogByKey = new Map(
  integrationCatalog.map((integration) => [integration.key, integration])
);

const getIntegrationCatalogItem = (provider) =>
  integrationCatalogByKey.get(getCanonicalProviderKey(provider));

const getTrend = (metric) => {
  const label = metric?.label || "";
  const changePercent = metric?.change_percent;

  if (changePercent === null || changePercent === undefined) {
    return {
      text: `N/A ${label}`.trim(),
      tone: "neutral",
    };
  }

  const trend = metric?.trend || "flat";
  const sign = trend === "down" ? "-" : trend === "up" ? "+" : "";

  return {
    text: `${sign}${formatNumber(Math.abs(changePercent))}% ${label}`.trim(),
    tone: trend === "down" ? "negative" : trend === "up" ? "positive" : "neutral",
  };
};

const getChartData = (periodData) =>
  (Array.isArray(periodData?.series) ? periodData.series : []).map((item, index) => ({
    label: item?.label || item?.key || `Point ${index + 1}`,
    captured: Number.isFinite(Number(item?.count)) ? Number(item.count) : 0,
  }));

const getLeadItems = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.leads)) return value.leads;
  if (Array.isArray(value?.data)) return value.data;
  if (value?.data) return getLeadItems(value.data);
  return [];
};

const getAnsweredCount = (item) => {
  const answerSources = [item, item?.membership_qna].filter(
    (source) => source && typeof source === "object"
  );
  const explicitCount = answerSources
    .flatMap((source) => [
      source.questions_answered,
      source.answered,
      source.answered_count,
      source.answers_count,
      source.answer_count,
    ])
    .map(Number)
    .find((value) => Number.isFinite(value) && value >= 0);
  const answers = answerSources.flatMap((source) => [
    source.answer1,
    source.answer2,
    source.answer3,
  ]);
  const questionAnswers = Array.isArray(item?.questions)
    ? item.questions.map((question) => question?.answer ?? question?.value)
    : [];
  const derivedCount = [...answers, ...questionAnswers].filter(
    (answer) => answer !== null && answer !== undefined && String(answer).trim()
  ).length;

  return Math.max(explicitCount ?? 0, derivedCount);
};

const formatLeadTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) === 1 ? "" : "s"} ago`;
  return formatDate(value);
};

const normalizeLeads = (value) =>
  getLeadItems(value).map((item, index) => {
    const groupValue = item?.group ?? item?.source_group ?? item?.group_name;
    const groupId = item?.group_id ?? item?.source_group_id ?? item?.groupId ?? (
      typeof groupValue === "object" ? groupValue?.id ?? groupValue?.group_id : ""
    );

    return {
      id: item?.id ?? item?.lead_id ?? index,
      name: item?.name ?? item?.member_name ?? item?.full_name ?? item?.lead_name ?? "Unknown lead",
      email: item?.email ?? item?.email_address ?? item?.profile_email ?? "",
      group: typeof groupValue === "object" ? groupValue?.name : groupValue || "Unknown group",
      groupUrl: groupId ? `https://www.facebook.com/groups/${encodeURIComponent(groupId)}` : "",
      answered: getAnsweredCount(item),
      time: formatLeadTime(item?.time ?? item?.created_at ?? item?.createdAt ?? item?.timestamp ?? item?.date),
      profileUrl: item?.profile_url ?? item?.profileUrl ?? item?.facebook_url ?? item?.url ?? "",
    };
  });

function MetricCard({ label, value, note, trend, tone, integrations, onViewIntegrations, providers = [] }) {
  if (value === undefined) {
    return (
      <Card className="lead-metric-card" variant="ui">
        <CardHeader className="lead-metric-card-header">
          <div className="lead-metric-label-row"><span>{label}</span></div>
        </CardHeader>
        <CardContent className="lead-metric-card-content">
          <Skeleton className="lead-metric-value-skeleton" />
          <Skeleton className="lead-metric-detail-skeleton" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="lead-metric-card" variant="ui">
      <CardHeader className="lead-metric-card-header">
        <div className="lead-metric-label-row">
        <span>{label}</span>
        {integrations && (
          <DashboardTooltip content="View connected integrations" side="top">
            <Button
              aria-label="View connected integrations"
              className="integration-link-icon"
              onClick={onViewIntegrations}
              size="icon-xs"
              type="button"
              variant="ui"
            >
              <ArrowUpRight aria-hidden="true" size={15} strokeWidth={1.8} />
            </Button>
          </DashboardTooltip>
        )}
        </div>
      </CardHeader>
      <CardContent className="lead-metric-card-content">
        <strong>{value}</strong>
        {note && (
          <Badge className="lead-metric-note" variant="outline">
            {note}
          </Badge>
        )}
        {trend && (
          <Badge
            className={`lead-trend-badge ${tone === "negative" ? "is-negative" : ""} ${tone === "neutral" ? "is-neutral" : ""}`}
            icon={tone === "negative" ? <ArrowDown size={12} strokeWidth={2.8} /> : tone === "positive" ? <ArrowUp size={12} strokeWidth={2.8} /> : null}
            variant={tone === "negative" ? "critical" : tone === "positive" ? "success" : "outline"}
          >
            {trend}
          </Badge>
        )}
        {integrations && (
        <span className="integration-bubbles" aria-label={`${providers.length} connected integrations`} role="group">
            {providers.map((provider, index) => {
              const catalogItem = getIntegrationCatalogItem(provider);
              const providerLabel = catalogItem?.label || provider;

              return (
                <DashboardTooltip
                  content={providerLabel}
                  key={`${provider}-${index}`}
                  side="top"
                  wrapperClassName="integration-bubble-tooltip-wrapper"
                >
                  <span
                    aria-label={providerLabel}
                    className="integration-bubble"
                    role="img"
                    style={{ zIndex: providers.length - index }}
                    tabIndex="0"
                  >
                    {catalogItem?.icon ? <img loading="lazy" alt="" aria-hidden="true" src={catalogItem.icon} /> : getProviderLabel(provider)}
                  </span>
                </DashboardTooltip>
              );
            })}
          </span>
        )}
      </CardContent>
    </Card>
  );
}

function ChartTooltipContent({ active, payload, label }) {
  const reduce = useReducedMotion();
  const plotArea = usePlotArea();
  const yScale = useYAxisScale();

  if (!active || !payload?.length) return null;

  const value = Number(payload[0].value) || 0;
  const pointY = yScale?.(value) ?? plotArea?.y ?? 0;
  const tooltipOffsetY = pointY - 8;

  return (
    <motion.div
      animate={{ y: tooltipOffsetY }}
      className="lead-chart-tooltip-anchor"
      initial={{ y: tooltipOffsetY }}
      transition={{
        type: "spring",
        stiffness: 380,
        damping: 30,
        mass: 0.7,
      }}
    >
      <span className="lead-chart-tooltip-offset">
        <motion.div
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          className="lead-chart-tooltip"
          initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.9, filter: "blur(5px)" }}
          key={label}
          transition={reduce ? { duration: 0.14, ease: EASE_OUT } : {
            type: "spring",
            stiffness: 380,
            damping: 30,
            mass: 0.7,
            opacity: { duration: 0.14, ease: EASE_OUT },
            filter: { duration: 0.18, ease: EASE_OUT },
          }}
        >
          <span>{label}</span>
          <div><span>Captured</span><strong>{formatNumber(payload[0].value)}</strong></div>
        </motion.div>
      </span>
    </motion.div>
  );
}

function Dashboard({ dashboardData, error, getDashboardData, getDashboardLeads, isLoading, redirect, status, recentLeads: recentLeadsData, recentLeadsError, recentLeadsLoading }) {
  const navigate = useNavigate();
  const [range, setRange] = useState("monthly");
  const [group, setGroup] = useState("All Groups");
  const [search, setSearch] = useState("");
  const [copiedEmailLeadId, setCopiedEmailLeadId] = useState(null);
  const [copiedProfileLeadId, setCopiedProfileLeadId] = useState(null);
  const [isSetupVideoOpen, setIsSetupVideoOpen] = useState(false);

  useEffect(() => {
    if (!status) {
      getDashboardData();
      getDashboardLeads();
    }
  }, [getDashboardData, getDashboardLeads, status]);

  useEffect(() => {
    if (redirect) navigate("/login");
  }, [navigate, redirect]);

  const normalizedLeads = useMemo(() => normalizeLeads(recentLeadsData), [recentLeadsData]);

  const visibleLeads = useMemo(() => {
    const term = search.trim().toLowerCase();
    return normalizedLeads.filter((lead) => {
      const matchesGroup = group === "All Groups" || lead.group === group;
      const matchesSearch = !term || `${lead.name} ${lead.email} ${lead.group}`.toLowerCase().includes(term);
      return matchesGroup && matchesSearch;
    });
  }, [group, normalizedLeads, search]);

  const leadGroups = useMemo(
    () => [...new Set(normalizedLeads.map((lead) => lead.group).filter(Boolean))],
    [normalizedLeads]
  );

  const copyValue = async (value, leadId, setCopiedLeadId) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedLeadId(leadId);
      window.setTimeout(() => setCopiedLeadId((currentLeadId) => currentLeadId === leadId ? null : currentLeadId), 1400);
    } catch (error) {
      setCopiedLeadId((currentLeadId) => currentLeadId === leadId ? null : currentLeadId);
    }
  };

  const clearRecentLeadFilters = () => {
    setSearch("");
    setGroup("All Groups");
  };

  const summary = dashboardData?.summary || {};
  const leadGrowth = dashboardData?.lead_growth;
  const lifetime = summary.total_leads_lifetime || {};
  const thisMonth = summary.total_leads_this_month || {};
  const today = summary.total_leads_today || {};
  const integrations = summary.total_connected_integrations || {};
  const providers = Array.isArray(integrations.providers) ? integrations.providers : [];
  const topGroups = Array.isArray(dashboardData?.top_groups) ? dashboardData.top_groups : [];
  const chartData = getChartData(leadGrowth?.[range]);
  const hasChartActivity = chartData.some((item) => item.captured > 0);
  const activeTopGroups = topGroups.filter((item) => Number(item.lead_count) > 0);
  const hasTopGroupActivity = activeTopGroups.length > 0;
  const hasRecentLeads = normalizedLeads.length > 0;
  const hasRecentLeadFilters = Boolean(search.trim() || group !== "All Groups");
  const chartMax = Math.max(1, ...chartData.map((item) => item.captured));
  const monthTrend = getTrend(thisMonth);
  const todayTrend = getTrend(today);
  const leadsPending = recentLeadsLoading || (recentLeadsData === undefined && !status);

  useEffect(() => {
    const defaultPeriod = leadGrowth?.default_period === "yearly" ? "yearly" : "monthly";
    setRange(defaultPeriod);
  }, [leadGrowth?.default_period]);

  return (
    <main className="lead-dashboard-page">
      <section className="lead-dashboard-shell" aria-labelledby="lead-overview-title">
        <div className="lead-dashboard-heading dashboard-page-heading dashboard-page-heading--title-only"><h1 className="dashboard-page-title" id="lead-overview-title">Dashboard</h1></div>

        <div className="lead-metrics-grid">
          <MetricCard label="Total leads captured" value={isLoading && !dashboardData ? undefined : dashboardData ? formatNumber(lifetime.count) : "—"} note={lifetime.since ? `Since ${formatDate(lifetime.since)}` : null} />
          <MetricCard label="Total leads this month" value={isLoading && !dashboardData ? undefined : dashboardData ? formatNumber(thisMonth.count) : "—"} trend={dashboardData ? monthTrend.text : null} tone={monthTrend.tone} />
          <MetricCard label="Total leads today" value={isLoading && !dashboardData ? undefined : dashboardData ? formatNumber(today.count) : "—"} trend={dashboardData ? monthTrend.text : null} tone={todayTrend.tone} />
          <MetricCard label="Connected Integrations" value={isLoading && !dashboardData ? undefined : dashboardData ? formatNumber(integrations.count) : "—"} integrations onViewIntegrations={() => navigate("/user/integration")} providers={providers} />
        </div>

        <div className="lead-overview-grid">
          <Card className="lead-panel lead-trend-panel" variant="ui">
            <CardHeader className="lead-panel-heading">
              <CardTitle id="captured-title"><h2>Total leads captured</h2></CardTitle>
              <Tabs className="lead-range-tabs" onValueChange={setRange} value={range}>
                <TabsList aria-label="Lead chart range">
                  <TabsTrigger value="yearly">This Year</TabsTrigger>
                  <TabsTrigger value="monthly">This Month</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent className="lead-chart-content">
              <div className="lead-chart-wrap">
              {hasChartActivity ? (
                <ResponsiveContainer height="100%" width="100%">
                  <AreaChart data={chartData} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="leadAreaFill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#33cdd1" stopOpacity={0.12} />
                        <stop offset="100%" stopColor="#33cdd1" stopOpacity={0.01} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#eaf1f4" vertical />
                    <XAxis axisLine={false} dataKey="label" tick={{ fill: "#2c3e4a", fontSize: 12 }} tickLine={false} tickMargin={14} />
                    <YAxis allowDecimals={false} axisLine={false} domain={[0, chartMax]} tick={{ fill: "#2c3e4a", fontSize: 12 }} tickCount={6} tickFormatter={formatCompactNumber} tickLine={false} tickMargin={8} width={42} />
                    <RechartsTooltip
                      animationDuration={180}
                      animationEasing="ease-out"
                      content={<ChartTooltipContent />}
                      cursor={{ stroke: "#33cdd1", strokeDasharray: "3 3" }}
                      position={{ y: 8 }}
                    />
                    <Area dataKey="captured" fill="url(#leadAreaFill)" isAnimationActive={false} stroke="#33cdd1" strokeWidth={1.5} type="monotone" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : isLoading ? (
                <Skeleton className="lead-panel-skeleton lead-chart-skeleton" />
              ) : error ? (
                <div className="dashboard-inline-empty">Unable to load lead growth.</div>
              ) : (
                <EmptyState
                  primaryActionIcon={CirclePlay}
                  primaryActionLabel="Watch setup video"
                  compact
                  description="Follow the setup guide to start capturing leads and see your growth over time."
                  icon={ChartNoAxesCombined}
                  onPrimaryAction={() => setIsSetupVideoOpen(true)}
                  onSecondaryAction={() => window.open(HELP_ARTICLE_URL, "_blank", "noopener,noreferrer")}
                  secondaryActionIcon={BookOpen}
                  secondaryActionLabel="Open help article"
                  title="No lead activity yet"
                />
              )}
              </div>
            </CardContent>
          </Card>

          <Card className="lead-panel top-groups-panel" variant="ui">
            <CardHeader className="lead-panel-heading">
              <CardTitle id="top-groups-title"><h2>Top Groups</h2></CardTitle>
              {/* <Link className="see-all-link" to="/user/data-table">See all {dashboardData ? formatNumber(summary.total_groups?.total) : "—"} groups <ArrowRight size={17} /></Link> */}
            </CardHeader>
            <CardContent className="top-groups-content">
              <ul className="top-groups-list">
                {activeTopGroups.map((item, index) => (
                  <li className="top-group-row" key={item.group_id || item.name}>
                    <span><strong>{item.name || "Unnamed group"}</strong><small>{formatNumber(item.lead_count)} leads{item.integration_count ? ` · ${formatNumber(item.integration_count)} integrations` : ""}</small></span>
                    <em className="top-group-rank">#{item.rank || index + 1}</em>
                  </li>
                ))}
              </ul>
              {!hasTopGroupActivity && isLoading && (
                <div className="top-groups-skeleton-list">
                  {Array.from({ length: 3 }, (_, index) => <Skeleton className="top-group-skeleton" key={index} />)}
                </div>
              )}
              {!hasTopGroupActivity && !isLoading && error && <div className="dashboard-inline-empty">Unable to load top groups.</div>}
              {!hasTopGroupActivity && !isLoading && !error && (
                <EmptyState
                  primaryActionIcon={CirclePlay}
                  primaryActionLabel="Watch setup video"
                  compact
                  description="Capture leads from your groups to see which groups are performing best."
                  icon={UsersRound}
                  onPrimaryAction={() => setIsSetupVideoOpen(true)}
                  onSecondaryAction={() => window.open(HELP_ARTICLE_URL, "_blank", "noopener,noreferrer")}
                  secondaryActionIcon={BookOpen}
                  secondaryActionLabel="Open help article"
                  title="No top groups yet"
                />
              )}
            </CardContent>
          </Card>
        </div>

        <section className="recent-leads-section" aria-labelledby="recent-leads-title">
          <div className="recent-leads-heading">
            <h2 id="recent-leads-title">Recent Leads</h2>
            <div className="recent-leads-actions">
              <label className="sr-only" htmlFor="lead-group-filter">Filter leads by group</label>
              <Select className="lead-select-wrap" onValueChange={setGroup} value={group}>
                <SelectTrigger aria-label="Filter leads by group" id="lead-group-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Groups">All Groups</SelectItem>
                  {leadGroups.map((leadGroup) => <SelectItem key={leadGroup} value={leadGroup}>{leadGroup}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="lead-search-wrap">
                <span className="sr-only">Search leads</span>
                <Input
                  aria-label="Search leads"
                  className="lead-search-input"
                  classNames={{ input: "lead-search-input", root: "lead-search-input-shell" }}
                  leftIcon={<Search size={17} />}
                  onValueChange={setSearch}
                  placeholder="Search recent leads"
                  value={search}
                  variant="ui"
                />
              </label>
            </div>
          </div>

          <div className="recent-leads-table-wrap">
            <Table className="recent-leads-table" variant="ui">
              <TableHeader><TableRow><TableHead>FULL NAME</TableHead><TableHead>LEAD</TableHead><TableHead>SOURCE GROUP</TableHead><TableHead>ANSWERED</TableHead><TableHead>MEMBER SINCE</TableHead></TableRow></TableHeader>
              <TableBody>
                {leadsPending && Array.from({ length: 4 }, (_, rowIndex) => (
                  <TableRow key={`recent-leads-loading-${rowIndex}`}>
                    {Array.from({ length: 5 }, (_, cellIndex) => (
                      <TableCell key={cellIndex}><Skeleton className="recent-lead-row-skeleton" /></TableCell>
                    ))}
                  </TableRow>
                ))}
                {!leadsPending && recentLeadsError && <TableRow><TableCell className="empty-leads" colSpan="6"><span>{recentLeadsError}</span> <Button onClick={() => getDashboardLeads()} size="sm" type="button" variant="ui">Retry</Button></TableCell></TableRow>}
                {!leadsPending && !recentLeadsError && visibleLeads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell><div className="lead-full-name-cell"><strong className="lead-full-name">{lead.name}</strong><DashboardTooltip content={copiedProfileLeadId === lead.id ? "Copied!" : "Copy profile URL"} side="top"><Button aria-label={copiedProfileLeadId === lead.id ? `Profile URL copied for ${lead.name}` : `Copy profile URL for ${lead.name}`} className="copy-profile-button" disabled={!lead.profileUrl} onClick={() => copyValue(lead.profileUrl, lead.id, setCopiedProfileLeadId)} size="icon-sm" type="button" variant="ui">{copiedProfileLeadId === lead.id ? <CopyCheck aria-hidden="true" size={13} /> : <Copy aria-hidden="true" size={13} />}</Button></DashboardTooltip></div></TableCell>
                    <TableCell><div className="lead-email-cell"><span title={lead.email}>{lead.email || "—"}</span><DashboardTooltip content={copiedEmailLeadId === lead.id ? "Copied!" : "Copy email"} side="top"><Button aria-label={copiedEmailLeadId === lead.id ? `Email copied for ${lead.name}` : `Copy email for ${lead.name}`} className="copy-email-button" disabled={!lead.email} onClick={() => copyValue(lead.email, lead.id, setCopiedEmailLeadId)} size="icon-sm" type="button" variant="ui">{copiedEmailLeadId === lead.id ? <CopyCheck aria-hidden="true" size={13} /> : <Copy aria-hidden="true" size={13} />}</Button></DashboardTooltip></div></TableCell>
                    <TableCell><div className="lead-group-cell"><span className="source-group-tag">{lead.group}</span><DashboardTooltip content="Open group on Facebook" side="top">{lead.groupUrl && <Button aria-label={`Open ${lead.group} on Facebook`} className="open-group-link" onClick={() => window.open(lead.groupUrl, "_blank", "noopener,noreferrer")} size="icon-xs" type="button" variant="ui"><ArrowUpRight aria-hidden="true" size={14} /></Button>}</DashboardTooltip></div></TableCell>
                    <TableCell>{formatNumber(lead.answered)}</TableCell><TableCell>{lead.time || "—"}</TableCell>
                  </TableRow>
                ))}
                {!leadsPending && !recentLeadsError && !hasRecentLeads && (
                  <TableRow>
                    <TableCell className="empty-leads empty-state-cell" colSpan="5">
                      <EmptyState
                        primaryActionIcon={CirclePlay}
                        primaryActionLabel="Watch setup video"
                        compact
                        description="Complete the setup guide and start capturing leads."
                        icon={Inbox}
                        onPrimaryAction={() => setIsSetupVideoOpen(true)}
                        onSecondaryAction={() => window.open(HELP_ARTICLE_URL, "_blank", "noopener,noreferrer")}
                        secondaryActionIcon={BookOpen}
                        secondaryActionLabel="Open help article"
                        title="No recent leads yet"
                      />
                    </TableCell>
                  </TableRow>
                )}
                {!leadsPending && !recentLeadsError && hasRecentLeads && hasRecentLeadFilters && !visibleLeads.length && (
                  <TableRow>
                    <TableCell className="empty-leads empty-state-cell" colSpan="5">
                      <EmptyState
                        primaryActionLabel="Clear filters"
                        compact
                        description="Try a different search or clear the current filters."
                        icon={SearchX}
                        onPrimaryAction={clearRecentLeadFilters}
                        title="No matching leads"
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <Button className="all-leads-link" onClick={() => navigate("/user/data-table")} type="button" variant="ui"><ArrowRight aria-hidden="true" size={17} />All Leads</Button>
        </section>
      </section>
      {isSetupVideoOpen && (
        <HelpVideoDialog
          articleUrl={HELP_ARTICLE_URL}
          description="Watch the quick setup walkthrough, then use the help article whenever you need the written steps."
          onClose={() => setIsSetupVideoOpen(false)}
          title="Set up Groupboss"
          videoUrl={SETUP_VIDEO_URL}
        />
      )}
    </main>
  );
}

export default Dashboard;
